import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSignIn as clerkUseSignIn, useSignUp as clerkUseSignUp } from '@clerk/clerk-react';
import AppLoader from '../../components/AppLoader/AppLoader';
import { useAuthUser } from '../../hooks/useAuthUser';
import {
	clearStoredClerkCallbackUrl,
	getStoredClerkCallbackUrl,
	resolveAuthDestinationFromLocation
} from '../../utils/authRedirect';

const isTestAuthEnabled = process.env.REACT_APP_USE_TEST_AUTH === 'true';
const AUTH_CALLBACK_TIMEOUT_MS = 15000;

const AuthCallback = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const { user, isLoaded: authLoaded } = useAuthUser();
	const { isLoaded: signInLoaded, handleRedirectCallback: handleSignInRedirect } = clerkUseSignIn();
	const { isLoaded: signUpLoaded, handleRedirectCallback: handleSignUpRedirect } = clerkUseSignUp();
	const [callbackError, setCallbackError] = useState('');
	const hasHandled = useRef(false);
	const callbackUrl = useMemo(() => {
		return getStoredClerkCallbackUrl() || window.location.href;
	}, []);

	const finalDestination = useMemo(() => {
		return resolveAuthDestinationFromLocation(location);
	}, [location]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!hasHandled.current) {
				setCallbackError('Authentication is taking too long. Please try signing in again.');
			}
		}, AUTH_CALLBACK_TIMEOUT_MS);
		return () => clearTimeout(timeout);
	}, []);

	useEffect(() => {
		if (!authLoaded || !user || hasHandled.current) return;

		hasHandled.current = true;
		clearStoredClerkCallbackUrl();
		navigate(finalDestination, { replace: true });
	}, [authLoaded, finalDestination, navigate, user]);

	useEffect(() => {
		let isActive = true;
		(async () => {
			if (hasHandled.current) return;

			if (isTestAuthEnabled) {
				hasHandled.current = true;
				clearStoredClerkCallbackUrl();
				navigate(finalDestination, { replace: true });
				return;
			}

			let handled = false;
			const canHandleSignIn = signInLoaded && typeof handleSignInRedirect === 'function';
			const canHandleSignUp = signUpLoaded && typeof handleSignUpRedirect === 'function';

			try {
				if (canHandleSignIn) {
					await handleSignInRedirect(callbackUrl);
					if (!isActive) return;
					handled = true;
					hasHandled.current = true;
					navigate(finalDestination, { replace: true });
					return;
				}
			} catch (error) {
				console.error('Clerk sign-in callback failed:', error);
			}

			try {
				if (canHandleSignUp) {
					await handleSignUpRedirect(callbackUrl);
					if (!isActive) return;
					handled = true;
					hasHandled.current = true;
					navigate(finalDestination, { replace: true });
				}
			} catch (error) {
				console.error('Clerk sign-up callback failed:', error);
			} finally {
				if (handled) {
					clearStoredClerkCallbackUrl();
				} else if (signInLoaded && signUpLoaded && !handled) {
					setCallbackError('Unable to complete sign in. Please try again.');
				}
			}
		})();
		return () => {
			isActive = false;
		};
	}, [signInLoaded, signUpLoaded, handleSignInRedirect, handleSignUpRedirect, navigate, finalDestination, callbackUrl]);

	if (callbackError) {
		return (
			<div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '1rem', padding: '2rem', textAlign: 'center' }}>
				<p style={{ fontSize: '1rem', color: 'var(--text-secondary, #666)' }}>{callbackError}</p>
				<button
					onClick={() => navigate(`/login?redirectTo=${encodeURIComponent(finalDestination)}`, { replace: true })}
					style={{ padding: '0.625rem 1.5rem', borderRadius: '10px', border: 'none', background: 'var(--primary, #3b82f6)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.9rem' }}
				>
					Back to Login
				</button>
			</div>
		);
	}

	return <AppLoader message='Finishing sign in...' />;
};

export default AuthCallback;








